// src/pages/SignUpPage.js
import React, { useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  EyeIcon,
  EyeSlashIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import ErrorBanner from "../ui/ErrorBanner";
import Spinner from "../ui/Spinner";
import {
  getPasswordRequirements,
  getPasswordStrength,
  getLockColor,
  getConfirmLockColor,
} from "../utils/passwordValidation";
import { consumeRedirectAfterAuth } from "../utils/redirectAfterAuth";

export default function SignUpPage() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const requirements = useMemo(() => getPasswordRequirements(password), [password]);
  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const lockColor = useMemo(() => getLockColor(password, requirements, strength), [password, requirements, strength]);
  const confirmLockColor = useMemo(() => getConfirmLockColor(confirmPassword, password), [confirmPassword, password]);

  const passwordsMatch = confirmPassword === password;
  const canSubmit =
    !submitting &&
    agreedToTerms &&
    requirements.allOk &&
    passwordsMatch &&
    confirmPassword.length > 0;

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);

    try {
      const results = await signup({
        firstName,
        lastName,
        email,
        password,
        passwordConfirm: confirmPassword,
      });

      // In mock mode, user is auto-logged in, go to the intended destination (or home).
      if (process.env.REACT_APP_USE_MOCK_API === "true") {
        navigate(consumeRedirectAfterAuth() || "/home");
      } else {
        // In real mode, navigate to verification page. The intended destination
        // lives in sessionStorage (set by ProtectedRoute), so VerifyEmailPage
        // reads it directly — no need to thread it through router state.
        navigate("/verify-email", { state: { email } });
      }
    } catch (error) {
      setError(error.message || "Signup failed. Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-elevated px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <a href="/" className="text-3xl font-bold text-text-primary">
            Traces
          </a>
          <p className="text-sm text-text-muted mt-1">Create a new account</p>
        </div>

        <form onSubmit={handleSignUp} className="space-y-6">
          {error && (
            <ErrorBanner
              message={error}
              onRetry={() => handleSignUp({ preventDefault: () => {} })}
              onDismiss={() => setError(null)}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              First Name
            </label>
            <input
              type="text"
              className="w-full bg-surface-elevated border border-border-strong rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-black focus:border-black disabled:opacity-50"
              placeholder="John"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Last Name
            </label>
            <input
              type="text"
              className="w-full bg-surface-elevated border border-border-strong rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-black focus:border-black disabled:opacity-50"
              placeholder="Doe"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Email
            </label>
            <input
              type="email"
              className="w-full bg-surface-elevated border border-border-strong rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-black focus:border-black disabled:opacity-50"
              placeholder="johndoe@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Password
            </label>
            <div className="relative">
              <LockClosedIcon
                className={`absolute top-1/2 left-2.5 -translate-y-1/2 h-4 w-4 ${lockColor}`}
                aria-hidden="true"
              />
              <input
                type={showPassword ? "text" : "password"}
                className="w-full bg-surface-elevated border border-border-strong rounded-md pl-9 pr-10 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-black focus:border-black disabled:opacity-50"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={submitting}
              />
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 right-2 -translate-y-1/2 h-8 w-8 inline-grid place-items-center rounded-full text-text-muted hover:text-text-primary transition focus:outline-none"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {password && !requirements.allOk && (
              <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-xs">
                <p className="font-medium mb-1">Password needs the following:</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  {requirements.list
                    .filter((r) => !r.ok)
                    .map((r) => (
                      <li key={r.id}>{r.label}</li>
                    ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <LockClosedIcon
                className={`absolute top-1/2 left-2.5 -translate-y-1/2 h-4 w-4 ${confirmLockColor}`}
                aria-hidden="true"
              />
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="w-full bg-surface-elevated border border-border-strong rounded-md pl-9 pr-10 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-black focus:border-black disabled:opacity-50"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={submitting}
              />
              <button
                type="button"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute top-1/2 right-2 -translate-y-1/2 h-8 w-8 inline-grid place-items-center rounded-full text-text-muted hover:text-text-primary transition focus:outline-none"
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="mt-1 text-xs text-destructive">
                Passwords do not match.
              </p>
            )}
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="agree-terms"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              disabled={submitting}
              className="mt-1 h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <label
              htmlFor="agree-terms"
              className="text-xs text-text-secondary leading-relaxed"
            >
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </a>
            </label>
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-primary text-text-inverse py-2 px-4 rounded-md text-sm hover:bg-gemini-accent transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <Spinner size={16} />}
            {submitting ? "Signing up..." : "Sign Up"}
          </button>
        </form>

        <div className="text-center text-sm text-text-secondary mt-6">
          Already have an account?{" "}
          <Link
            to="/login"
            state={location.state}
            className="text-black hover:underline"
          >
            Log in
          </Link>
        </div>
      </div>
    </div>
  );
}
